import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Conta os itens do carrinho pela mesma regra de snippets/cart-bubble.liquid.
 *
 * As propriedades sao gravadas por measurement-pricing.js no "adicionar ao
 * carrinho" de produto sob medida, entao chegam em /cart.js. A regra do Liquid
 * tambem olha a tag 'sob-medida' do produto, que /cart.js nao expoe - na pratica
 * nao faz falta, porque o produto com essa tag so entra no carrinho pela
 * calculadora, que sempre grava _measurement_product.
 *
 * @param {{items?: Array<{quantity: number, properties?: Record<string, string>}>}} cart
 * @returns {number}
 */
function countCartItems(cart) {
  let total = 0;

  for (const item of cart?.items ?? []) {
    const properties = item.properties ?? {};

    // Linhas auxiliares de ajuste de preco nao sao itens para o cliente.
    if (properties._is_measurement_adjustment === 'true') continue;

    const isMeasurement = properties._measurement_product === 'true' || properties._sob_medida === 'true';

    // No item sob medida a quantidade e area, nao numero de pecas.
    total += isMeasurement ? 1 : item.quantity;
  }

  return total;
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   *
   * O `itemCount` que vem no evento e o `cart.item_count` cru do Shopify, que
   * nao serve para esta loja: um capacho sob medida entra como uma linha
   * principal cuja quantidade representa AREA, mais linhas auxiliares cujas
   * quantidades representam CENTAVOS de ajuste de preco. Somar tudo isso da um
   * numero sem sentido na bolha (centenas), enquanto o servidor, via
   * snippet/cart-bubble.liquid, mostra corretamente 1.
   *
   * Por isso a contagem e refeita aqui a partir de /cart.js, pela mesma regra
   * do Liquid. Em caso de falha na rede, cai no valor do evento.
   *
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    let itemCount;

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((response) =>
        response.json()
      );

      itemCount = countCartItems(cart);
    } catch {
      itemCount = event.detail.data?.itemCount ?? 0;
    }

    // Sempre absoluto: o valor acima ja e o total do carrinho, entao somar o
    // que veio do formulario de produto duplicaria a conta.
    this.renderCartBubble(itemCount, false);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
